import { apiSlice } from "./http";
import { Tags } from "../enum/tags";
import {
  IGamificationParams,
  IValidatePeriodParams,
  ISlugAvailability,
  ISlugAvailabilityParams,
  ICreateGamification,
  IUpdateGamification,
  IMechanicConfig,
  ISegmentationInvalidRow,
  ISegmentationResult,
  IGamificationCampaignDto,
  IGamificationListResponse,
  ICompetingCampaignsParams,
  ICompetingCampaignsResponse,
  IBannerGalleryItem,
  IBannerGalleryParams,
  IBannerGalleryResponse,
  IConcurrencyGroup,
  IConcurrencyGroupsParams,
  IConcurrencyGroupsResponse,
  IGamificationSetting,
  IGamificationSettingResponse,
  IGamificationSettingsResponse,
  IUpdateGamificationSettingParams,
} from "@/@types/gamification";
import {
  GAMIFICATION_ENTITY_TYPE,
  IGamificationAuditResponse,
} from "@/@types/gamification-audit";
import { ICampaignSummary } from "@/@types/gamification-report";
import { COMPETING_CAMPAIGNS_PAGE_SIZE } from "@/pages/crm/gamification/constants/competing-campaigns";
import { collectCompetingCampaigns } from "@/pages/crm/gamification/utils/competing-campaigns-pages";

export interface ICreateGamificationResponse {
  message: string;
  data: {
    id: number;
    idCapa?: number;
  } & Record<string, unknown>;
}

function appendMechanicConfig(
  formData: FormData,
  mechanicConfig?: IMechanicConfig,
) {
  if (mechanicConfig) {
    formData.append("mechanicConfig", JSON.stringify(mechanicConfig));
  }
}

function appendFaqItems(
  formData: FormData,
  faqItems?: { question: string; answer: string }[],
) {
  if (faqItems !== undefined) {
    formData.append("faqItems", JSON.stringify(faqItems));
  }
}

function appendBanner(
  formData: FormData,
  field: "banner3x4" | "banner16x9",
  file?: File,
  sourceKey?: string,
) {
  if (file instanceof File) formData.append(field, file);
  if (sourceKey) formData.append(`${field}SourceKey`, sourceKey);
}

function normalizeSegmentationReport(response: unknown): ISegmentationResult {
  const raw = response as
    | { data?: Record<string, unknown> }
    | Record<string, unknown>
    | undefined;
  const data = ((raw as { data?: Record<string, unknown> })?.data ??
    raw ??
    {}) as Record<string, unknown>;

  const validRaw = data.valid;
  const validCpfs: string[] = Array.isArray(validRaw)
    ? validRaw
        .map((item) => String(item).replace(/\D/g, ""))
        .filter((cpf) => cpf.length > 0)
    : [];
  const valid = Array.isArray(validRaw)
    ? validRaw.length
    : Number(validRaw ?? 0);

  const invalidRaw = data.invalid;
  const invalidRows: ISegmentationInvalidRow[] = Array.isArray(invalidRaw)
    ? (invalidRaw as ISegmentationInvalidRow[])
    : ((data.invalidRows as ISegmentationInvalidRow[]) ?? []);

  return {
    total: Number(data.total ?? 0),
    valid,
    invalid: Array.isArray(invalidRaw)
      ? invalidRaw.length
      : Number(invalidRaw ?? invalidRows.length),
    duplicates: Number(data.duplicates ?? 0),
    invalidRows,
    validCpfs,
  };
}

export const gamificationService = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getGamifications: builder.query<IGamificationListResponse, IGamificationParams>({
      query: ({ meta, search, status, orderBy, campaignName, startDate, endDate, sortBy, sortOrder, includeDeleted, campaignType }) => {
        const perPage = meta.perPage ?? 10;
        const params = new URLSearchParams({
          page: meta.page.toString(),
          limit: perPage.toString(),
        });

        if (search) params.append('search', search);
        if (campaignName) params.append('campaignName', campaignName);
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);
        if (status) params.append('status', status);
        if (sortBy) params.append('sortBy', sortBy);
        if (sortOrder) params.append('sortOrder', sortOrder);
        if (includeDeleted) params.append('includeDeleted', includeDeleted.toString());
        if (orderBy) params.append('orderBy', orderBy);
        if (campaignType) params.append('campaignType', campaignType);

        return {
          url: `/gamification-campaign?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: [Tags.GAMIFICATION],
    }),

    getCompetingCampaigns: builder.query<
      ICompetingCampaignsResponse,
      ICompetingCampaignsParams
    >({
      queryFn: ({ startDate, endDate }, _api, _extra, fetchWithBQ) =>
        collectCompetingCampaigns(async (page: number) => {
          const params = new URLSearchParams({
            page: page.toString(),
            limit: COMPETING_CAMPAIGNS_PAGE_SIZE.toString(),
            startDate,
            endDate,
          });
          const response = await fetchWithBQ({
            url: `/gamification-campaign?${params.toString()}`,
            method: "GET",
          });
          if (response.error) return { error: response.error };
          return { data: response.data as IGamificationListResponse };
        }),
      providesTags: [Tags.GAMIFICATION],
    }),

    searchGamificationCampaigns: builder.query<
      { id: number; campaignName: string; status: string; startDate: string; endDate: string }[],
      { q: string; limit?: number }
    >({
      query: ({ q, limit = 5 }) => {
        const params = new URLSearchParams({
          q,
          limit: limit.toString(),
        });

        return {
          url: `/gamification-campaign/search?${params.toString()}`,
          method: "GET",
        };
      },
      transformResponse: (response: { message: string; data: Record<string, unknown>[]; total: number }) =>
        response.data.map((item: Record<string, unknown>) => ({
          id: item.id as number,
          campaignName: item.campaignName as string,
          status: item.status as string,
          startDate: item.startDate as string,
          endDate: item.endDate as string,
        })),
      providesTags: [Tags.GAMIFICATION],
    }),

    getGamificationById: builder.query<{ message: string; data: IGamificationCampaignDto }, number>({
      query: (id) => ({
        url: `/gamification-campaign/${id}`,
        method: "GET",
      }),
      providesTags: [Tags.GAMIFICATION],
    }),

    getGamificationStatus: builder.query<
      { id: number; status: string; isActive: boolean; message: string },
      number
    >({
      query: (id) => ({
        url: `/gamification-campaign/campaign-status/${id}`,
        method: "GET",
      }),
      providesTags: [Tags.GAMIFICATION],
    }),

    validateGamificationPeriod: builder.query<
      { valid: boolean; message: string },
      IValidatePeriodParams
    >({
      query: ({ startDate, endDate, excludeId, campaignType, competes, group }) => {
        const params = new URLSearchParams({
          startDate,
          endDate,
        });

        if (excludeId !== undefined) {
          params.append("excludeId", excludeId.toString());
        }

        if (campaignType) {
          params.append("campaignType", campaignType);
        }

        if (competes !== undefined) {
          params.append("competes", String(competes));
        }

        if (competes && group) {
          params.append("group", group);
        }

        return {
          url: `/gamification-campaign/validate-period?${params.toString()}`,
          method: "GET",
        };
      },
    }),

    checkSlugAvailability: builder.query<
      ISlugAvailability,
      ISlugAvailabilityParams
    >({
      query: ({ slug, excludeId }: ISlugAvailabilityParams) => ({
        url: "/gamification-campaign/slug-availability",
        method: "GET",
        params: { slug, excludeId },
      }),

      keepUnusedDataFor: 0,
    }),

    createGamification: builder.mutation<
      ICreateGamificationResponse,
      ICreateGamification
    >({
      query: (data) => {
        const formData = new FormData();

        formData.append('campaignName', data.campaignName);
        if (data.slug) formData.append('slug', data.slug);
        formData.append('startDate', data.startDate);
        formData.append('endDate', data.endDate);

        if (data.campaignType) {
          formData.append('campaignType', data.campaignType);
        }

        if (data.status) {
          formData.append('status', data.status);
        }

        const stores = data.stores.map(store => ({
          code: parseInt(store.storeCode),
          name: store.storeName,
          nickname: store.nickname
        }));
        formData.append('stores', JSON.stringify(stores));

        if (data.allStoresSelected !== undefined) {
          formData.append('allStoresSelected', data.allStoresSelected.toString());
        }

        appendMechanicConfig(formData, data.mechanicConfig);
        appendFaqItems(formData, data.faqItems);

        if (data.regulations && data.regulations instanceof File) {
          formData.append('regulationFile', data.regulations);
        }

        appendBanner(formData, 'banner3x4', data.banner3x4, data.banner3x4SourceKey);
        appendBanner(formData, 'banner16x9', data.banner16x9, data.banner16x9SourceKey);

        return {
          url: "/gamification-campaign",
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: [Tags.GAMIFICATION],
    }),

    updateGamification: builder.mutation<
      { message: string; data: Record<string, unknown> },
      { id: number; data: IUpdateGamification }
    >({
      query: ({ id, data }) => {
        const formData = new FormData();

        if (data.campaignName) formData.append('campaignName', data.campaignName);

        if (data.slug !== undefined) formData.append('slug', data.slug);
        if (data.startDate) formData.append('startDate', data.startDate);
        if (data.endDate) formData.append('endDate', data.endDate);
        if (data.campaignType) formData.append('campaignType', data.campaignType);

        if (data.status) formData.append('status', data.status);

        if (data.stores) {
          const stores = data.stores.map(store => ({
            code: parseInt(store.storeCode),
            name: store.storeName,
            nickname: store.nickname
          }));
          formData.append('stores', JSON.stringify(stores));
        }

        if (data.allStoresSelected !== undefined) {
          formData.append('allStoresSelected', data.allStoresSelected.toString());
        }

        appendMechanicConfig(formData, data.mechanicConfig);
        appendFaqItems(formData, data.faqItems);

        if (data.regulations && data.regulations instanceof File) {
          formData.append('regulationFile', data.regulations);
        }

        appendBanner(formData, 'banner3x4', data.banner3x4, data.banner3x4SourceKey);
        appendBanner(formData, 'banner16x9', data.banner16x9, data.banner16x9SourceKey);

        return {
          url: `/gamification-campaign/${id}`,
          method: "PATCH",
          body: formData,
        };
      },
      invalidatesTags: [Tags.GAMIFICATION],
    }),

    getBannerGallery: builder.query<IBannerGalleryItem[], IBannerGalleryParams>({
      query: ({ campaignType, search }) => {
        const params = new URLSearchParams();
        if (campaignType) params.append("campaignType", campaignType);
        if (search) params.append("search", search);
        const queryString = params.toString();

        return {
          url: queryString
            ? `/gamification-campaign/banner-gallery?${queryString}`
            : "/gamification-campaign/banner-gallery",
          method: "GET",
        };
      },
      transformResponse: (response: IBannerGalleryResponse) => response.data ?? [],
      providesTags: [Tags.GAMIFICATION],
    }),

    getConcurrencyGroups: builder.query<IConcurrencyGroup[], IConcurrencyGroupsParams>({
      query: ({ search, excludeCampaignId }) => {
        const params = new URLSearchParams();
        if (search) params.append("search", search);
        if (excludeCampaignId) params.append("excludeCampaignId", String(excludeCampaignId));
        const queryString = params.toString();

        return {
          url: queryString
            ? `/gamification-campaign/concurrency-groups?${queryString}`
            : "/gamification-campaign/concurrency-groups",
          method: "GET",
        };
      },
      transformResponse: (response: IConcurrencyGroupsResponse) => response.data ?? [],
      providesTags: [Tags.GAMIFICATION],
    }),

    getGamificationSettings: builder.query<IGamificationSetting[], void>({
      query: () => ({
        url: "/gamification-settings",
        method: "GET",
      }),
      transformResponse: (response: IGamificationSettingsResponse) => response.data ?? [],
      providesTags: [Tags.GAMIFICATION_SETTINGS],
    }),

    updateGamificationSetting: builder.mutation<
      IGamificationSetting,
      IUpdateGamificationSettingParams
    >({
      query: ({ key, value }) => ({
        url: `/gamification-settings/${key}`,
        method: "PUT",
        body: { value },
      }),
      transformResponse: (response: IGamificationSettingResponse) => response.data,
      invalidatesTags: [Tags.GAMIFICATION_SETTINGS],
    }),

    inactivateGamification: builder.mutation<void, number>({
      query: (id) => ({
        url: `/gamification-campaign/${id}`,
        method: "PATCH",
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: 'INACTIVE'
        })
      }),
      invalidatesTags: [Tags.GAMIFICATION],
    }),

    validateSegmentation: builder.mutation<ISegmentationResult, { file: File }>({
      query: ({ file }) => {
        const formData = new FormData();
        formData.append("file", file);

        return {
          url: "/gamification-campaign/segmentation/validate",
          method: "POST",
          body: formData,
        };
      },
      transformResponse: normalizeSegmentationReport,
    }),

    uploadSegmentation: builder.mutation<
      ISegmentationResult,
      { campaignId: number; file: File }
    >({
      query: ({ campaignId, file }) => {
        const formData = new FormData();
        formData.append("file", file);

        return {
          url: `/gamification-campaign/${campaignId}/segmentation`,
          method: "POST",
          body: formData,
        };
      },
      transformResponse: normalizeSegmentationReport,
      invalidatesTags: [Tags.GAMIFICATION],
    }),

    getGamificationHistory: builder.query<IGamificationAuditResponse, number>({
      query: (idCapa) => ({
        url: `/audit-log/entity/${GAMIFICATION_ENTITY_TYPE}/${idCapa}`,
        method: "GET",
      }),
      providesTags: [Tags.GROUP_AUDIT],
    }),

    getGamificationSummary: builder.query<ICampaignSummary, number>({
      query: (id) => ({
        url: `/gamification-campaign/${id}/summary`,
        method: "GET",
      }),
      providesTags: [Tags.GAMIFICATION],
    }),
  }),
  overrideExisting: true,
});

export const {
  useGetGamificationsQuery,
  useGetCompetingCampaignsQuery,
  useSearchGamificationCampaignsQuery,
  useGetGamificationByIdQuery,
  useGetGamificationStatusQuery,
  useLazyValidateGamificationPeriodQuery,
  useCheckSlugAvailabilityQuery,
  useCreateGamificationMutation,
  useUpdateGamificationMutation,
  useInactivateGamificationMutation,
  useGetBannerGalleryQuery,
  useGetConcurrencyGroupsQuery,
  useGetGamificationSettingsQuery,
  useUpdateGamificationSettingMutation,
  useValidateSegmentationMutation,
  useUploadSegmentationMutation,
  useLazyGetGamificationHistoryQuery,
  useLazyGetGamificationSummaryQuery,
} = gamificationService;

export const {
  endpoints: {
    getGamifications: { select: selectGetGamifications },
  },
} = gamificationService;
